import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import {
  Star,
  ArrowLeft,
  User,
  Phone,
  MapPin,
  Save,
  LogOut,
  Shield,
  FileText,
  Award,
  Bell,
  MessageSquare,
  ChevronRight,
  Calendar
} from "lucide-react";
import PrivacyNotice from "../components/PrivacyNotice";
import PeerSupportDisclaimer from "../components/PeerSupportDisclaimer";
import PushNotificationSettings from "../components/PushNotificationSettings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: user?.name || "",
    role: user?.role || "veteran",
    phone: user?.phone || "",
    address: user?.address || "",
    bio: user?.bio || "",
    is_available: user?.is_available || false
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await axios.put(`${API}/users/me`, formData, {
        withCredentials: true
      });
      setUser(response.data);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Profile update error:", error.response?.data || error.message);
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/");
    }
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-card border-b border-border z-40">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Star className="w-6 h-6 text-primary" />
              <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                Profile
              </span>
              <span className="text-xs text-primary font-bold">™</span>
            </div>
          </div>
          <Button 
            onClick={handleSave}
            disabled={isSaving}
            size="sm"
            data-testid="save-profile-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </div>
      </header>

      <main className="pt-16 pb-8 px-4 max-w-2xl mx-auto">
        <div className="space-y-6 py-6">
          {/* Profile Header */}
          <Card className="card-tactical">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                    {getInitials(formData.name || user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="text-muted-foreground text-sm">{user?.email}</p>
                  <Badge variant="secondary" className="mt-2">
                    {formData.role === "veteran" ? "Veteran" : 
                     formData.role === "responder" ? "Responder" : 
                     formData.role === "pending_responder" ? "Pending Responder" : "Veteran"}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personal Information */}
          <Card className="card-tactical">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <User className="w-4 h-4 text-secondary" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="label-uppercase">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address" className="label-uppercase">Address</Label>
                <Input
                  id="address"
                  type="text"
                  placeholder="City, State or full address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  data-testid="address-input"
                />
                <p className="text-sm text-muted-foreground">
                  Used to help match you with nearby responders
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio" className="label-uppercase">Bio (Optional)</Label>
                <Textarea
                  id="bio"
                  placeholder="Tell us a bit about yourself..."
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="min-h-[100px]"
                  data-testid="bio-input"
                />
                <p className="text-sm text-muted-foreground">
                  Share your background, interests, or anything you'd like others to know
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Role Selection */}
          <Card className="card-tactical">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <User className="w-4 h-4 text-secondary" />
                Account Type
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="label-uppercase">Your Role</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="veteran">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        <span>Veteran - I need support</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="responder">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>Responder - I want to help</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  {formData.role === "veteran" 
                    ? "As a veteran, you can request support from peer responders."
                    : "As a responder, you can accept and respond to crisis requests."}
                </p>
              </div>

              {formData.role === "responder" && (
                <div className="pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Availability Status</Label>
                      <p className="text-sm text-muted-foreground">
                        Turn on to appear available for crisis requests
                      </p>
                    </div>
                    <Switch
                      checked={formData.is_available}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_available: checked })}
                      data-testid="availability-switch"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="card-tactical">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Phone className="w-4 h-4 text-secondary" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="label-uppercase">Phone Number (Optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="phone-input"
                />
                <p className="text-sm text-muted-foreground">
                  Used for phone/video call support sessions
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Responder-Only Settings */}
          {(user?.role === "responder") && (
            <>
              {/* Push Notifications */}
              <PushNotificationSettings />

              {/* Quick Links */}
              <Card className="card-tactical">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Shield className="w-4 h-4 text-primary" />
                    Responder Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <button
                    onClick={() => navigate("/badges")}
                    className="w-full flex items-center justify-between p-4 rounded-lg hover:bg-muted/50 transition-colors"
                    data-testid="badges-link"
                  >
                    <div className="flex items-center gap-3">
                      <Award className="w-5 h-5 text-yellow-500" />
                      <div className="text-left">
                        <p className="font-medium">My Badges</p>
                        <p className="text-sm text-muted-foreground">View earned achievements</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </button>

                  <button
                    onClick={() => navigate("/sms-preferences")}
                    className="w-full flex items-center justify-between p-4 rounded-lg hover:bg-muted/50 transition-colors"
                    data-testid="sms-prefs-link"
                  >
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-5 h-5 text-green-500" />
                      <div className="text-left">
                        <p className="font-medium">SMS Preferences</p>
                        <p className="text-sm text-muted-foreground">Manage alert settings</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </button>

                  <button
                    onClick={() => navigate("/schedule")}
                    className="w-full flex items-center justify-between p-4 rounded-lg hover:bg-muted/50 transition-colors"
                    data-testid="schedule-link"
                  >
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-blue-500" />
                      <div className="text-left">
                        <p className="font-medium">Availability Schedule</p>
                        <p className="text-sm text-muted-foreground">Set weekly availability hours</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </button>
                </CardContent>
              </Card>
            </>
          )}

          {/* Privacy Notice */}
          <PrivacyNotice />

          {/* Danger Zone */}
          <Card className="card-tactical border-destructive/20">
            <CardHeader>
              <CardTitle className="text-base text-destructive" style={{ fontFamily: 'Manrope' }}>
                Session
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button 
                variant="outline"
                className="w-full text-destructive hover:bg-destructive hover:text-destructive-foreground"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
      
      {/* Global Footer Disclaimer */}
      <PeerSupportDisclaimer />
    </div>
  );
};

export default Profile;
